import { Controller } from "@hotwired/stimulus"

const submittingMap = new WeakMap()

export default class extends Controller {
  static targets = ["input", "sharedKey", "encryptedText", "iv", "form", "attachments"]

  connect() {
    submittingMap.set(this, false)

    if (!this.hasFormTarget) {
      this.formTarget = this.element.querySelector('[data-encrypt-submit-target="form"]')
    }
  }

  disconnect() {
    submittingMap.delete(this)
  }

  async manualSubmit(event) {
    event?.preventDefault?.()
    return this.performSubmit()
  }

  async submit(event) {
    event?.preventDefault?.()
    return this.performSubmit()
  }

  async performSubmit() {

    if (submittingMap.get(this)) {
      return
    }

    submittingMap.set(this, true)

    let input = null
    let draft = null
    const status = this.element.querySelector("[data-chat-send-status]")
    if (status) status.textContent = "Sending…"

    try {
      const find = (key) => this.element.querySelector(`[data-encrypt-submit-target="${key}"]`)
      input = find("input")
      const form = this.hasFormTarget ? this.formTarget : find("form")
      const encryptedTextField = find("encryptedText")
      const ivField = find("iv")
      const sharedKeyField = find("sharedKey")

      this.syncVoiceSkinFields(form)

      draft = input?.value || ""
      const text = draft.trim()
      const hasText = text.length > 0
      const attachmentsInput = this.element.querySelector('input[type="file"][name^="bubble[attachments"]')

      let hasAttachments = false
      
      if (attachmentsInput && attachmentsInput.files) {
        hasAttachments = attachmentsInput.files.length > 0
      } else {
      }

      let hasVoiceBlob = false

      const voiceInput = this.element.querySelector('input[name="bubble[voice_blob]"]')
      if (voiceInput?.files?.length > 0) {
        hasVoiceBlob = true
      }

      // Only encrypt text, skip if there's no text
      let originalInputName = null

      if (hasText) {
        const key = await waitForSessionKey({ extractable: true })
        const iv = crypto.getRandomValues(new Uint8Array(12))
        const encoded = new TextEncoder().encode(text)
        const encrypted = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, encoded)

        const encryptedTextB64 = btoa(String.fromCharCode(...new Uint8Array(encrypted)))
        const ivB64 = btoa(String.fromCharCode(...iv))
        const rawKey = await crypto.subtle.exportKey("raw", key)
        const keyB64 = btoa(String.fromCharCode(...new Uint8Array(rawKey)))

        ivField.value = ivB64
        encryptedTextField.value = encryptedTextB64
        sharedKeyField.value = keyB64


        // Prevent the browser from posting typed plaintext alongside the ciphertext.
        originalInputName = input?.getAttribute("name")
        if (input && originalInputName) {
          input.dataset.originalName = originalInputName
          input.removeAttribute("name")
        }
      }

      // Final check: prevent empty bubble
      const isEmpty = !hasText && !hasAttachments && !hasVoiceBlob
      if (isEmpty) {
        if (status) status.textContent = "Write a message first."
        submittingMap.set(this, false)
        return
      }

      if (form) {
        if (form.dataset.chatFast === "true") {
          await this.submitViaFetch(form)
        } else {
          if (form.hasAttribute("data-no-overlay")) {
            document.documentElement.dataset.skipNextOverlay = "true"
          }
          form.requestSubmit()
        }
      }

      if (input && hasText) {
        setTimeout(() => {
          if (input.dataset.originalName) {
            input.setAttribute("name", input.dataset.originalName)
            delete input.dataset.originalName
          }
          if (input.value === draft) input.value = ""
        }, 0)
      }

      if (status) status.textContent = "Sent."
    } catch (err) {
      if (status) status.textContent = "Message not sent. Your draft is saved here; please retry."
      if (input?.dataset?.originalName) {
        input.setAttribute("name", input.dataset.originalName)
        delete input.dataset.originalName
      }
    } finally {
      requestAnimationFrame(() => {
        submittingMap.set(this, false)
      })
    }
  }

  syncVoiceSkinFields(form) {
    if (!form) {
      return
    }

    const voiceControls = document.querySelector('[data-controller~="voice"]')
    if (!voiceControls) {
      return
    }

    const toggle = voiceControls.querySelector('[data-voice-target="toggle"]')
    const select = voiceControls.querySelector('select[name="voice_preference"]')
    const enabled = toggle?.dataset?.active === "true"
    const preference = select?.value || ""

    this.upsertHiddenField(form, "bubble[speaker_voice_enabled]", enabled ? "true" : "false")
    this.upsertHiddenField(form, "bubble[speaker_voice_preference]", preference)
  }

  upsertHiddenField(form, name, value) {
    let field = form.querySelector(`input[name="${name}"]`)
    if (!field) {
      field = document.createElement("input")
      field.type = "hidden"
      field.name = name
      form.appendChild(field)
    }

    field.value = value
  }

  async submitViaFetch(form) {
    const response = await fetch(form.action, {
      method: (form.method || "POST").toUpperCase(),
      body: new FormData(form),
      headers: {
        Accept: "application/json"
      },
      credentials: "same-origin"
    })

    if (!response.ok) {
      let details = `HTTP ${response.status}`

      try {
        const payload = await response.json()
        details = payload?.error || payload?.errors?.join?.(", ") || details
      } catch (_error) {
        // Keep fallback when JSON is unavailable.
      }

      throw new Error(details)
    }
  }
}
